/**
 * Issue #643 — THE MAP ASKS FOR WHAT IS ON SCREEN. Wave 4 of epic #609, and
 * the wave a user can feel.
 *
 * ## What this module is
 *
 * #640, #641 and #642 built a viewport contract on the server: a bounding
 * box snapped to a coarse grid, capped and flagged when it truncates, and
 * answered as aggregate CELLS instead of rows below a zoom threshold. Until
 * this slice, no caller sent a box — `GroundsMap.jsx` fetched the whole
 * directory once on mount and never again. That is the 11.70 MB / 404 D1
 * statements `api/test/mapScale.test.ts` has been measuring since #639.
 *
 * This module is the CLIENT half of that contract, and it is a module rather
 * than code inside the page for the reason #615 and #617 were both bitten
 * by: `GroundsMap.jsx` mounts leaflet, so this repo renders no component in
 * unit tests. A snapping rule written inside the page is a rule nothing can
 * assert — it can only be grepped. Everything here is an ordinary function
 * over ordinary values: the snap, the cache key, which mode a zoom is in,
 * the cache itself, the envelope reader and the truncation copy. The page
 * keeps the wiring (leaflet events, react state) and no decisions.
 *
 * ## The three numbers that are mirrored from the API, and how they stay true
 *
 * `VIEWPORT_GRID_DEGREES` is the SERVER's number. The client cannot import
 * it: `api/` is TypeScript compiled for Workers and CRA's module scope
 * refuses an import from outside `src/`. So it is restated here — and
 * `mapViewport.test.js` READS THE API SOURCE FILE OFF DISK and asserts the
 * mirror still equals the number it mirrors. A copied constant with no test
 * is drift waiting to happen (`formatKm`, #436); a copied constant pinned to
 * its original by a test that fails loudly is a copy that cannot rot.
 *
 * It is the ONLY mirrored number, and #643 is why there is only one. The
 * aggregate trigger used to be mirrored too, as a zoom; it is now a row
 * count the client cannot compute, so the client stopped guessing and reads
 * `mode` off the response instead. A number that has to be known in two
 * places is a liability; one that only the server can know is not.
 *
 * ## Why the client snaps too, when the server already does
 *
 * The server snaps regardless (`api/src/lib/viewport.ts`), so a client that
 * sent raw viewports would still get correct, cache-friendly answers. What
 * it would NOT do is stop asking: every pixel of pan is a distinct URL and
 * therefore a distinct request, even when the answer is byte-for-byte the
 * one already on screen. Snapping here turns "a small pan" into "the same
 * key", which the cache below then answers with no network at all. That is
 * #643's second acceptance criterion, and it is a claim about REQUESTS, not
 * about responses.
 */

/**
 * THE GRID — mirrors `VIEWPORT_GRID_DEGREES` in `api/src/lib/viewport.ts`,
 * which carries the whole derivation (0.25 deg is ~27.8 km of latitude, a
 * city; it is 2^-2 so the arithmetic is exact in IEEE-754 and two clients a
 * nanodegree apart produce the same key).
 *
 * Pinned to the API's copy by `mapViewport.test.js`.
 */
export const VIEWPORT_GRID_DEGREES = 0.25;

/**
 * THE CLIENT DOES NOT PREDICT THE MODE, and this is where a constant used to
 * be that let it try.
 *
 * #642 triggered the aggregate answer on `zoom < 5`, so the client could
 * mirror the number and know, before asking, which shape was coming back.
 * #643 re-triggered it on the ROW COUNT IN THE BOX
 * (`AGGREGATE_ROW_TRIGGER` in `api/src/db/repos/grounds.ts`) because the
 * zoom rule was derived from one directory size and aggregated a
 * hundred-ground pilot directory that should have been drawn as pins.
 *
 * A count is not knowable from the client, and that is fine — the mode was
 * always the server's to decide (#642 put `mode` on both contracts for
 * exactly this). What changed here is that the cache is keyed on the mode of
 * the answer that CAME BACK rather than on a guess about the one that will:
 * see `answerKey` below.
 */
export const MAP_MOUNT_ZOOM = 2;

/** WGS84. A box outside these is not a place, and the API refuses it. */
export const LATITUDE_LIMIT = 90;
export const LONGITUDE_LIMIT = 180;

/**
 * HOW LONG A PAN MUST SETTLE BEFORE THE MAP ASKS — 300 ms, derived from
 * leaflet's own timings and from what a person notices.
 *
 * The floor is leaflet's: a zoom animation runs 250 ms (`Map.ZoomAnimation`'s
 * default duration), and a scroll wheel spun two notches emits `zoomend`
 * twice, roughly one animation apart. A debounce shorter than 250 ms
 * therefore issues a request for a zoom level the user was passing THROUGH,
 * which is the exact waste this slice exists to remove. 300 ms clears it
 * with margin.
 *
 * The ceiling is human: ~400 ms after a gesture stops is where a delay stops
 * reading as "loading" and starts reading as "nothing happened". So the
 * usable band is narrow and 300 ms sits in it — long enough that a drag, its
 * inertia and a couple of wheel notches coalesce into ONE request, short
 * enough that pins appear while the hand is still on the map.
 *
 * It is a constant rather than a literal because it is a tuning decision
 * that a future measurement may move, and a number buried in a `setTimeout`
 * inside a component is a number nobody can find or test.
 */
export const VIEWPORT_DEBOUNCE_MS = 300;

/**
 * HOW MANY VIEWPORTS THE CLIENT REMEMBERS — 24, and it is one of TWO bounds.
 * See `VIEWPORT_CACHE_MAX_ROWS` directly below; both are needed and each
 * catches what the other cannot.
 *
 * 24 entries is about a 5x5 patch of snapped boxes around wherever the user
 * started, which is "pan two screens in any direction and back" — the motion
 * this cache exists to make free. It bounds the case rows cannot: a long
 * drag over ocean produces a stream of DIFFERENT keys holding ZERO rows
 * each, so a rows-only bound would let the map accumulate keys forever.
 */
export const VIEWPORT_CACHE_MAX_ENTRIES = 24;

/**
 * HOW MANY ROWS THE CLIENT REMEMBERS — 5,000, and this is the bound that
 * matters on a phone.
 *
 * An unbounded cache is epic #609's own defect relocated into the browser,
 * so this is derived rather than picked. The server caps one viewport read
 * at 1,000 rows (`GROUNDS_BBOX_MAX_ROWS`) of ~585 B, so five of those is
 * ~2.9 MB of retained JSON: the same order as the 1.03 MB page load a
 * 1,500-ground directory already costs today and well under the 11.70 MB
 * this epic is removing. An entry-count bound alone would admit 24 x 1,000 =
 * 24,000 rows, i.e. MORE than the unbounded read it replaces — which would
 * be a very funny way to finish this epic.
 *
 * Cells count toward the same budget at one row each. They are ~64 B against
 * a ground row's ~585 B so they will never be what evicts anything, and
 * counting them costs nothing and means no shape is exempt from the bound.
 */
export const VIEWPORT_CACHE_MAX_ROWS = 5_000;

/**
 * HOW FAR A CELL CLICK ZOOMS IN — three levels, and never less than the
 * threshold.
 *
 * A count bubble's only job is to be clicked, and the click has to make
 * PROGRESS. Three levels quarters the visible span on each axis, so a bubble
 * counting the grounds in a region resolves into either pins or a handful of
 * smaller bubbles in one tap rather than four. One level would leave the
 * user tapping repeatedly at a picture that barely changed; more than three
 * loses the context that told them where they were going.
 *
 * There is no "first zoom that draws pins" to aim at any more — since #643
 * that depends on how many grounds are in the box, not on the zoom — so the
 * step is chosen for legibility rather than to clear a threshold.
 */
export const CELL_ZOOM_STEP = 3;

/** Leaflet's own ceiling here: both basemaps declare `maxZoom: 19`. */
export const MAP_MAX_ZOOM = 19;

const clamp = (v, limit) => Math.min(limit, Math.max(-limit, v));

const isFiniteNumber = (v) => typeof v === "number" && Number.isFinite(v);

/**
 * Snap a box OUTWARD onto the grid — character for character the rule in
 * `api/src/lib/viewport.ts`, including the outward direction and the WGS84
 * clamp, because a client that snapped differently would ask for a box the
 * server then re-snapped to a THIRD box and every cache on the path would
 * miss.
 *
 * OUTWARD, never inward: the answer must be a superset of what is on
 * screen. A box snapped inward drops pins the user can see, which is a
 * correctness bug dressed up as a cache optimisation.
 *
 * Returns `null` for anything that is not four finite numbers, so a caller
 * cannot accidentally send `NaN` and receive the whole directory back.
 */
export function snapViewportBox(box) {
  if (!box) return null;
  const { minLat, minLng, maxLat, maxLng } = box;
  if (![minLat, minLng, maxLat, maxLng].every(isFiniteNumber)) return null;
  const g = VIEWPORT_GRID_DEGREES;
  const snapped = {
    minLat: clamp(Math.floor(minLat / g) * g, LATITUDE_LIMIT),
    minLng: clamp(Math.floor(minLng / g) * g, LONGITUDE_LIMIT),
    maxLat: clamp(Math.ceil(maxLat / g) * g, LATITUDE_LIMIT),
    maxLng: clamp(Math.ceil(maxLng / g) * g, LONGITUDE_LIMIT),
  };
  // A viewport whose edge sits EXACTLY on a grid line snaps to a box with no
  // area (floor and ceil agree), and the API refuses that as `bbox_inverted`
  // — correctly, since `min` must be strictly less than `max`. One grid cell
  // is the smallest honest widening, and it keeps the superset property.
  if (snapped.maxLat <= snapped.minLat) snapped.maxLat = Math.min(snapped.minLat + g, LATITUDE_LIMIT);
  if (snapped.maxLng <= snapped.minLng) snapped.maxLng = Math.min(snapped.minLng + g, LONGITUDE_LIMIT);
  return snapped;
}

/**
 * The map's current view as a snapped box and a zoom, or `null` when the map
 * cannot answer yet.
 *
 * Takes anything with `getBounds()` and `getZoom()`, so it is a leaflet map
 * in the product and a three-line object in the tests — which is the whole
 * reason this is not written inside `GroundsMap.jsx`.
 *
 * ## The antimeridian
 *
 * Leaflet reports longitudes UNWRAPPED: pan west past the date line and
 * `getWest()` keeps counting down through -180 to -190 and beyond, and a
 * world-width view reports a span of more than 360 degrees. Both are refused
 * by the API (`bbox_out_of_range`, and `bbox_inverted` for a box that wraps
 * the seam), so the client has to decide what to send.
 *
 * WIDEN TO THE WHOLE LONGITUDE RANGE, rather than split into two requests.
 * The latitude band still bounds the read, the answer is still a superset of
 * what is on screen (which is the property that matters — extra pins off the
 * edge cost nothing), and it stays ONE request with one answer to render.
 * Splitting the seam into two requests means merging two responses, two
 * truncation flags and two failure states in the render path, for a case no
 * pilot user can currently pan to; `api/src/lib/viewport.ts` names it as the
 * client's job and it remains available if anyone ever needs Fiji at zoom
 * 15. It is written down here rather than left as a surprise.
 */
export function viewportFromMap(map) {
  if (!map || typeof map.getBounds !== "function" || typeof map.getZoom !== "function") return null;
  const bounds = map.getBounds();
  const zoom = map.getZoom();
  if (!bounds || !isFiniteNumber(zoom)) return null;
  const south = bounds.getSouth();
  const north = bounds.getNorth();
  const west = bounds.getWest();
  const east = bounds.getEast();
  if (![south, north, west, east].every(isFiniteNumber)) return null;
  // The seam, and the >360 degree world view, both land here.
  const wrapped = east - west >= 2 * LONGITUDE_LIMIT || west < -LONGITUDE_LIMIT || east > LONGITUDE_LIMIT;
  const box = snapViewportBox({
    minLat: south,
    minLng: wrapped ? -LONGITUDE_LIMIT : west,
    maxLat: north,
    maxLng: wrapped ? LONGITUDE_LIMIT : east,
  });
  if (!box) return null;
  return { box, zoom: Math.round(zoom) };
}

/**
 * THE WHOLE WORLD, as a box. The FIRST question the map asks, and only the
 * first.
 *
 * ## Why the opening read is not the map's own bounds
 *
 * Because on the first paint the map's bounds mean nothing. `GroundsMap.jsx`
 * mounts at `center={[20, 30]} zoom={2}` — a hard-coded view over north
 * Africa that nobody chose and that #212 only ever tolerated because the page
 * immediately fitted itself to the directory it had downloaded. Reading
 * leaflet's bounds there and asking about THAT box answers a question the
 * user never asked: a player whose grounds are in New Zealand gets an empty
 * map, an empty card list and a search box that finds nothing, because their
 * venues are ninety degrees off the side of the opening frame. That is what
 * journeys 1, 15, 16 and 19 caught.
 *
 * So the first read asks about the world, the page fits itself to the answer
 * exactly as it always did, and every read after that is bounded by the
 * screen. The world box is NOT an unbounded read: it goes through the same
 * capped, snapped, mode-choosing contract as any other box, so the response
 * is at most `GROUNDS_BBOX_MAX_ROWS` rows or `AGGREGATE_MAX_CELLS` counts —
 * which is precisely the read `api/test/mapScale.test.ts` has measured as the
 * opening paint since #639, at 3,730 bytes for a 20,000-ground directory.
 *
 * ## What it costs, stated
 *
 * At pilot scale it fetches the whole directory, because at pilot scale the
 * whole directory IS what is on the world's screen — the same rows the page
 * read before this issue. The saving arrives when the directory outgrows the
 * cap, and then it arrives completely: 48 counts instead of 20,000 rows.
 */
export const WORLD_BOX = Object.freeze({
  minLat: -LATITUDE_LIMIT,
  minLng: -LONGITUDE_LIMIT,
  maxLat: LATITUDE_LIMIT,
  maxLng: LONGITUDE_LIMIT,
});

/**
 * THE WHOLE DIRECTORY AS A VIEWPORT — the world box at whatever zoom the map
 * is currently on. Asked in exactly ONE situation: **THE FIRST PAINT**,
 * before the user has moved the map at all (see the box's own header above).
 *
 * ## THERE WAS A SECOND CALLER, AND #644 REMOVED IT
 *
 * #643 also asked this WHILE A SEARCH WAS ACTIVE, on the argument that
 * "where is Thornmere Park" is a question about the directory rather than about
 * the frame — filtering only the rows in view means a search that finds
 * nothing whenever the ground is off screen, which is what the search box
 * exists to solve.
 *
 * The argument was right and the mechanism was a stated, bounded BRIDGE: the
 * world box is one capped, mode-choosing read, so a search over a directory
 * bigger than `GROUNDS_BBOX_MAX_ROWS` got counts rather than rows and the
 * page said "zoom in to search here" instead of lying. It reproduced the
 * pre-#643 reach for any directory that fitted in the cap, and no further.
 *
 * #644 replaced it with a server-side search (`lib/directorySearch.js`,
 * `hooks/useDirectorySearch.js`) that has no such ceiling: the term goes to
 * `GET /api/grounds?q=`, the whole directory is matched in SQL, and the
 * answer comes back paged. So the map went back to asking about exactly one
 * thing — what is on screen — and this function went back to one caller.
 *
 * The zoom is the map's own by default, because it is what sizes the cells if
 * the answer comes back aggregated.
 *
 * #1619 (D444): the new-look map mounts on New Zealand at zoom 5, and its
 * first-paint call passes `zoom` (`MAP_MOUNT_ZOOM`, 2) explicitly. The world
 * question is "what exists", the read `api/test/mapScale.test.ts` measures at
 * zoom 2 and the fit is drawn from; it is not the screen's read. Asked at the
 * map's own zoom 5 it would cut the planet into 4 degree cells and run into
 * `AGGREGATE_MAX_CELLS`. The screen's own read follows separately.
 */
export function worldViewport(map, zoom = undefined) {
  if (!map || typeof map.getZoom !== "function") return null;
  if (zoom !== undefined) return isFiniteNumber(zoom) ? { box: { ...WORLD_BOX }, zoom: Math.round(zoom) } : null;
  zoom = map.getZoom();
  if (!isFiniteNumber(zoom)) return null;
  return { box: { ...WORLD_BOX }, zoom: Math.round(zoom) };
}

const boxKey = (box) => `${box.minLat},${box.minLng},${box.maxLat},${box.maxLng}`;

/**
 * THE REQUEST KEY: the question, not the answer. Box plus zoom, because that
 * pair is exactly what goes on the wire.
 *
 * Used for two things and neither is the cache: skipping a request for the
 * viewport already on screen, and discarding a response that a later
 * viewport has already superseded.
 */
export function viewportKey(viewport) {
  if (!viewport || !viewport.box) return null;
  return `${boxKey(viewport.box)}|z${viewport.zoom}`;
}

/**
 * THE CACHE KEY: the answer, and the one asymmetry in it worth reading twice.
 *
 * A ROW answer does not depend on the zoom — the same box holds the same
 * grounds however far in you are — so its entry is keyed on the box alone
 * and a plain zoom-in inside one grid cell is a cache HIT for rows the page
 * is already holding.
 *
 * A CELL answer does: the cell size is `2^(7 - zoom)`, so the same box at
 * zoom 2 and at zoom 4 are two genuinely different sets of bubbles.
 *
 * The mode comes from the RESPONSE (`readViewportGrounds`), never from a
 * prediction — since #643 the server decides on a count the client cannot
 * see. `cachedAnswer` below is the read side of the same rule.
 */
export function answerKey(mode, viewport) {
  if (!viewport || !viewport.box) return null;
  return mode === "cells" ? `cells@${viewport.zoom}|${boxKey(viewport.box)}` : `rows|${boxKey(viewport.box)}`;
}

/**
 * The cached answer for a viewport, if either shape of it is held.
 *
 * ROWS FIRST, deliberately. A box whose row answer is cached is a box the
 * server said was inside the cap, and that verdict does not change with
 * zoom; a cell answer for the same box at this exact zoom is the fallback.
 * Trying only one of the two would make every zoom step inside a cached cell
 * a network round trip for an answer already in hand.
 */
export function cachedAnswer(cache, viewport) {
  if (!cache || !viewport) return undefined;
  return cache.get(answerKey("rows", viewport)) ?? cache.get(answerKey("cells", viewport));
}

/** Two viewports the page would answer identically. */
export function sameViewport(a, b) {
  const keyA = viewportKey(a);
  return keyA !== null && keyA === viewportKey(b);
}

/**
 * Issue #683 — is this point inside the box the map is currently showing?
 *
 * Used by `lib/directoryEnrich.js` to decide whether a played/saved venue
 * folded back onto the map (an archived venue the directory read itself no
 * longer carries) may be added while the map is in ROW mode with no search
 * active. Without this check the fold added the venue unconditionally, which
 * could include one a screen or a continent away — pulled into the map's own
 * FIT (`lib/mapFit.js`). Journey 15 caught exactly this: a venue folded in
 * from clear across the world dragged the fit to a point between Wellington
 * and London that was neither, and poisoned the fit's own dedup so a later,
 * legitimate search-fit was silently skipped.
 *
 * Inclusive on every edge: a venue sitting exactly on a snapped grid line is
 * still on screen, and `snapViewportBox` already widens outward so the box
 * is always a superset of what is actually visible.
 */
export function boxContainsPoint(box, lat, lng) {
  if (!box || !isFiniteNumber(lat) || !isFiniteNumber(lng)) return false;
  const { minLat, minLng, maxLat, maxLng } = box;
  if (![minLat, minLng, maxLat, maxLng].every(isFiniteNumber)) return false;
  return lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng;
}

/**
 * The query string both reads carry: the snapped box, and ALWAYS the zoom.
 *
 * The zoom is always sent even though the client already knows which mode it
 * implies, because mode selection is the server's decision (#642) and a
 * client that sent `zoom` only when it wanted cells would be making it. It
 * also means the threshold can move server-side without a client release.
 */
export function viewportQuery(viewport) {
  if (!viewport || !viewport.box) return "";
  const { minLat, minLng, maxLat, maxLng } = viewport.box;
  const bbox = `${minLat},${minLng},${maxLat},${maxLng}`;
  return `bbox=${encodeURIComponent(bbox)}&zoom=${encodeURIComponent(String(viewport.zoom))}`;
}

/**
 * Does the surfaces layer get fetched for this viewport?
 *
 * NO in cell mode, and this is a real saving rather than a shortcut: in cell
 * mode the map draws count bubbles and NO ground markers, and
 * `facilityMarkers` only ever draws a surface whose ground is on the map. So
 * a facilities read there fetches up to 1,000 rows (0.42 MB at 20,000
 * grounds) to draw exactly nothing.
 *
 * Taken off the RESPONSE's mode, not off the zoom: since #643 the server
 * decides on a row count, so "will there be markers" is only knowable once
 * the grounds answer is in hand. That ordering is `useDirectoryViewport`'s
 * — grounds first, surfaces only if rows came back.
 */
export function shouldLoadFacilities(mode) {
  return mode !== "cells";
}

/**
 * Read the grounds envelope, whichever shape came back.
 *
 * Normalises the two contracts into one object with BOTH arrays always
 * present — empty, never absent — so the page renders `grounds.map` and
 * `cells.map` unconditionally and cannot draw one mode's data through the
 * other's code path. `mode` is taken from the body (#642 put it on both
 * contracts for exactly this) and never inferred from which key is present.
 *
 * A bare ARRAY is still accepted and read as rows: that is the unbounded
 * response, which every other caller of `GET /api/grounds` still receives
 * and which this page would receive again if the box were ever dropped. A
 * client that threw on it would turn a partial regression into a blank page.
 */
export function readViewportGrounds(data) {
  if (Array.isArray(data)) {
    return { mode: "rows", grounds: data, cells: [], truncated: false, cap: null };
  }
  const body = data || {};
  const mode = body.mode === "cells" ? "cells" : "rows";
  return {
    mode,
    grounds: mode === "rows" && Array.isArray(body.grounds) ? body.grounds : [],
    cells: mode === "cells" && Array.isArray(body.cells) ? body.cells : [],
    truncated: body.truncated === true,
    // `row_cap` in row mode, `cell_cap` in cell mode — deliberately different
    // names on the wire (#642) so a client reading the wrong one gets
    // `undefined` rather than a plausible number.
    cap: mode === "cells" ? numberOrNull(body.cell_cap) : numberOrNull(body.row_cap),
  };
}

/** Read the facilities envelope, which has one shape (#641). */
export function readViewportFacilities(data) {
  if (Array.isArray(data)) return { facilities: data, truncated: false, cap: null };
  const body = data || {};
  return {
    facilities: Array.isArray(body.facilities) ? body.facilities : [],
    truncated: body.truncated === true,
    cap: numberOrNull(body.row_cap),
  };
}

function numberOrNull(v) {
  return isFiniteNumber(v) ? v : null;
}

/** `1000` -> `1,000`, without asking what locale the browser is in. */
const withThousands = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/**
 * WHAT THE SCREEN SAYS WHEN THE SERVER TRUNCATED — #609's "degradation is
 * visible", in words.
 *
 * The failure this prevents is not an error, it is a LIE OF OMISSION: the
 * server caps a viewport read at 1,000 grounds and says so in `truncated`,
 * and a client that ignores the flag draws an arbitrary 1,000 of 20,000 pins
 * as though they were all of them. Nobody can see that from the map. This
 * repo has fixed the same class three times (#617's "All grounds (1)",
 * #612's per-page counts, #608's failure-that-read-as-empty), and every time
 * the fix was the same: say the true thing on screen.
 *
 * Returns `null` when nothing was truncated — a notice that is always on
 * screen is a notice nobody reads.
 *
 * The three cases are three different sentences on purpose. "Areas" rather
 * than "grounds" in cell mode because a cell IS NOT a ground, and telling a
 * user "showing the first 500 grounds" when they are looking at 500 bubbles
 * counting 20,000 grounds would be false in both directions. The surfaces
 * case names surfaces, because the pins the user can see are all there and
 * it is only the violet ones that are short.
 */
export function viewportTruncationNotice(state) {
  if (!state) return null;
  const { mode, truncated, cap, facilitiesTruncated, facilitiesCap } = state;
  if (truncated && mode === "cells") {
    return `Showing the first ${withThousands(cap || 0)} areas here — zoom in for the rest.`;
  }
  if (truncated) {
    return `Showing the first ${withThousands(cap || 0)} grounds here — zoom in for the rest.`;
  }
  if (facilitiesTruncated) {
    return `Showing the first ${withThousands(facilitiesCap || 0)} surfaces here — zoom in for the rest.`;
  }
  return null;
}

/**
 * Where a click on a count bubble takes the map. Never past leaflet's own
 * ceiling, and never backwards — a click that zoomed out would be a trap.
 */
export function cellZoomTarget(zoom) {
  const from = isFiniteNumber(zoom) ? zoom : 0;
  return Math.min(from + CELL_ZOOM_STEP, MAP_MAX_ZOOM);
}

/**
 * A clicked bubble, as a `lib/mapFit.js` FIT rather than a raw map call.
 *
 * The page never calls `flyTo`/`setView` itself — #212 put every motion
 * decision in `mapFit.js` so the reduced-motion rule lives in exactly one
 * place, and `GroundsMap.test.js` asserts the page contains no motion call at
 * all. A cell click is a recentring like any other, so it goes through the
 * same door and inherits that rule for free.
 *
 * `kind: "point"` with `pointCount: 1` is `computeMapFit`'s single-result
 * shape, deliberately reused: the destination of a bubble click IS a single
 * point (the mean of the pins it counts, `GroundCell` in the API), just at a
 * zoom this module chooses instead of `SINGLE_RESULT_ZOOM`.
 */
export function cellFit(cell, fromZoom) {
  if (!cell || !isFiniteNumber(cell.latitude) || !isFiniteNumber(cell.longitude)) {
    return { kind: "none", pointCount: 0 };
  }
  return {
    kind: "point",
    center: [cell.latitude, cell.longitude],
    zoom: cellZoomTarget(fromZoom),
    pointCount: 1,
  };
}

/**
 * A BOUNDED cache of viewport answers, keyed by `viewportKey`.
 *
 * Least-recently-USED, not least-recently-written: `get` re-inserts, so the
 * boxes a user keeps returning to survive and the one they panned through
 * once does not. A `Map` preserves insertion order, so eviction is
 * `keys().next()` and there is no bookkeeping to get wrong.
 *
 * TWO BOUNDS, both enforced on every write — see `VIEWPORT_CACHE_MAX_ENTRIES`
 * and `VIEWPORT_CACHE_MAX_ROWS` for the derivations. `size(value)` says what
 * an entry costs in rows; the default counts `grounds`, `cells` and
 * `facilities`, which is every array any answer carries.
 *
 * An entry LARGER than the row budget on its own is still stored, and then
 * immediately evicts everything else including itself on the next write.
 * Refusing to store it would be worse: the page would hold the answer it is
 * currently rendering in a cache that claims not to have it, and the next
 * identical viewport would re-fetch it. It cannot happen with today's caps
 * (1,000 rows against a 5,000 budget) and the behaviour is asserted anyway.
 */
export function createViewportCache(options) {
  const opts = options || {};
  const maxEntries = opts.maxEntries || VIEWPORT_CACHE_MAX_ENTRIES;
  const maxRows = opts.maxRows || VIEWPORT_CACHE_MAX_ROWS;
  const sizeOf = opts.size || defaultEntrySize;
  const entries = new Map();
  let rows = 0;

  const drop = (key) => {
    const held = entries.get(key);
    if (held === undefined) return;
    rows -= sizeOf(held);
    entries.delete(key);
  };

  const evictWhileOverBudget = () => {
    while (entries.size > 0 && (entries.size > maxEntries || rows > maxRows)) {
      const oldest = entries.keys().next().value;
      // Stop rather than spin if the only entry left is itself over budget:
      // see the header — the answer on screen stays cached.
      if (entries.size === 1) return;
      drop(oldest);
    }
  };

  return {
    get(key) {
      if (key === null || key === undefined || !entries.has(key)) return undefined;
      const value = entries.get(key);
      // Re-insert: this is what makes it least-recently-USED.
      entries.delete(key);
      entries.set(key, value);
      return value;
    },
    set(key, value) {
      if (key === null || key === undefined) return;
      drop(key);
      entries.set(key, value);
      rows += sizeOf(value);
      evictWhileOverBudget();
    },
    has(key) {
      return entries.has(key);
    },
    clear() {
      entries.clear();
      rows = 0;
    },
    get size() {
      return entries.size;
    },
    get rows() {
      return rows;
    },
    keys() {
      return [...entries.keys()];
    },
  };
}

function defaultEntrySize(value) {
  if (!value) return 0;
  const count = (list) => (Array.isArray(list) ? list.length : 0);
  return count(value.grounds) + count(value.cells) + count(value.facilities);
}
